// Developer: Ankit Gupta
/**
 * An owner's review of a patch made for a consultant: once, as a whole, before anyone may
 * record that it was deployed. Shown on the patch in its conversation, on the Patches page
 * and in the Approvals inbox; the server decides who may press it.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Input, Popover, Space, Tag } from 'antd';
import { CheckOutlined, RollbackOutlined } from '@ant-design/icons';
import { useLanguage, useT } from '@/i18n';
import { orionApi } from './api';

/** The colour of each review state; its words are chat:review.<STATE>. */
export const REVIEW: Record<string, { color: string } | undefined> = {
  NEEDED: { color: 'gold' },
  APPROVED: { color: 'green' },
  REJECTED: { color: 'red' },
};

export function ReviewTag({ state }: { state?: string | null }) {
  const { t } = useT('chat');
  const r = REVIEW[state ?? ''];
  return r ? <Tag color={r.color}>{t(`review.${state}`)}</Tag> : null;
}

/** The patches waiting for this person's review, for the inbox and the menu's count. */
export function useWaitingReviews() {
  return useQuery({
    queryKey: ['orion-patches', 'review-needed'],
    queryFn: async () => (await orionApi.patches(undefined, undefined, 'NEEDED')).filter((p) => p.can_review),
    refetchInterval: 20_000,
    staleTime: 8_000,
    retry: false,
  });
}

export function ReviewButtons({ patchId, name, onDone }: { patchId: number; name: string; onDone?: () => void }) {
  const { t } = useT('chat');
  const rtl = useLanguage() === 'ar';
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const review = useMutation({
    mutationFn: (approve: boolean) => orionApi.reviewPatch(patchId, approve, note.trim() || undefined),
    onSuccess: () => {
      setOpen(false); setNote(''); setError(null);
      void qc.invalidateQueries({ queryKey: ['orion-patches'] });
      void qc.invalidateQueries({ queryKey: ['chat-thread'] });
      onDone?.();
    },
    onError: (e) => setError(((e as { response?: { data?: { detail?: string } } }).response?.data?.detail) ?? t('review.errRecord')),
  });
  return (
    <Space size={6} wrap>
      <Button type="primary" size="small" icon={<CheckOutlined />} loading={review.isPending} onClick={() => review.mutate(true)}>{t('review.approve')}</Button>
      <Popover
        open={open} onOpenChange={setOpen} trigger="click" placement={rtl ? 'bottomRight' : 'bottomLeft'} title={t('review.sendBackTitle', { name })}
        content={(
          <div style={{ width: 320 }}>
            <Input.TextArea autoFocus rows={3} maxLength={600} value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('review.notePlaceholder')} aria-label={t('review.noteAria')} />
            <Button danger size="small" style={{ marginTop: 8 }} disabled={note.trim().length < 3} loading={review.isPending} onClick={() => review.mutate(false)}>{t('review.sendBack')}</Button>
          </div>
        )}
      >
        <Button size="small" danger icon={<RollbackOutlined />} disabled={review.isPending}>{t('review.sendBack')}</Button>
      </Popover>
      {error && <span className="chat-card-answer is-no">{error}</span>}
    </Space>
  );
}
